import authApi from "../../api/auth"
import firebase from "firebase/app"
import { consoleBaseUrl } from "../../configs"

// initialize state
const initState = () => ({
  hd: "",
  user: "",
  userFsId: "",
  isNewUser: false,
  locale: "",
  lastName: "",
  firstName: "",
  displayName: "",
  idToken: "",
  mobile: "",
  oauthGrantParams: {}
})

const state = initState()

// getters
const getters = {
  /**
   * Get of hd
   * @param {object} state - current state
   */
  getHd(state) {
    return state.hd
  },

  /**
   * Get of user
   * @param {object} state - current state
   */
  getUser(state) {
    return state.user
  },

  /**
   * Get of user
   * @param {object} state - current state
   */
  getUserFsId(state) {
    return state.userFsId
  },

  /**
   * Get of isnewuser
   * @param {object} state - current state
   */
  getIsNewUser(state) {
    return state.isNewUser
  },

  /**
   * Get of locale
   * @param {object} state - current state
   */
  getLocale(state) {
    return state.locale
  },

  /**
   * Get of lastname
   * @param {object} state - current state
   */
  getLastName(state) {
    return state.lastName
  },

  /**
   * Get of firstname
   * @param {object} state - current state
   */
  getFirstName(state) {
    return state.firstName
  },

  /**
   * Get of firstname
   * @param {object} state - current state
   */
  getDisplayName(state) {
    return state.displayName
  },

  /**
   * Get of id token
   * @param {object} state - current state
   */
  getIdToken(state) {
    return state.idToken
  },

  /**
   * Get mobile
   */
  getMobile(state) {
    return state.mobile
  },

  /**
   * Returns the oauth grant params.
   */
  getOauthGrantRequestParams() {
    return JSON.parse(sessionStorage.getItem("oauthGrantParams"))
  }
}

// mutations
const mutations = {
  /**
   * Update the state of hd
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setHd(state, payload) {
    state.hd = payload
  },

  /**
   * Update the state of user
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setUser(state, payload) {
    state.user = payload
  },

  /**
   * Update the state of user
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setUserFsId(state, payload) {
    state.userFsId = payload
  },

  /**
   * Update the state of isnewuser
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setIsNewUser(state, payload) {
    state.isNewUser = payload
  },

  /**
   * Update the state of locale
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setLocale(state, payload) {
    state.locale = payload
  },

  /**
   * Update the state of lastname
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setLastName(state, payload) {
    state.lastName = payload
  },

  /**
   * Update the state of firstname
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setFirstName(state, payload) {
    state.firstName = payload
  },

  /**
   * Update the state of displayname
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setDisplayName(state, payload) {
    state.displayName = payload
  },

  /**
   * Update the state of idToken
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setIdToken(state, payload) {
    state.idToken = payload
  },

  /**
   * Set mobile
   *
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setMobile(state, payload) {
    state.mobile = payload
  },

  /**
   * Set the oauth grant payload.
   *
   * @param {object} state - current state
   * @param {*} payload - new value
   */
  setOauthGrantRequestParams(state, payload) {
    state.oauthGrantParams = payload
    sessionStorage.setItem("oauthGrantParams", JSON.stringify(payload))
  },

  /**
   * Reset the auth state.
   *
   * @param {object} state - Current state.
   */
  reset(state) {
    // get initial state
    const newState = initState()
    Object.entries(newState).forEach(([key, value]) => {
      state[key] = value
    })
  }
}

// actions
const actions = {
  /**
   * Process user objects.
   */
  async processUser({ commit, dispatch }, user) {
    // get claims
    const { claims } = await user.getIdTokenResult()

    // get id token
    const idToken = await user.getIdToken()
    console.log("jwt", idToken)
    // commits
    commit("setUser", user)
    commit("setUserFsId", claims.userFsId)
    commit("setDisplayName", user.displayName)
    commit("setIdToken", idToken || "")

    // bind user
    await dispatch("users/bindDoc", claims.userFsId, { root: true })
  },

  /**
   * Sign in/up with email and password.
   *
   * @param {object} context - Store context.
   * @param {object} payload - Email and password.
   */
  async continueWithEmailAndPassword(context, { email }) {
    const actionCodeSettings = {
      // URL you want to redirect back to. The domain for this
      // URL must be in the authorized domains list in the Firebase Console.
      url: `${consoleBaseUrl}/auth/cb/email-signup`,

      // This must be true.
      handleCodeInApp: true
    }

    try {
      // try to sign up
      await firebase.auth().sendSignInLinkToEmail(email, actionCodeSettings)

      // will pull at the email callback auth view
      localStorage.setItem("emailForSignIn", email)
      // commit("setIsNewUser", true)
    } catch (e) {
      console.debug("unable to sign up", e)
    }
  },

  /**
   * Sign in with external provider
   *
   * @param {object} provider - The provider popup
   */
  async signInWithPopup({ commit }, provider) {
    // sign in to google
    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)
    const result = await firebase.auth().signInWithPopup(provider)

    // collect information
    console.log("results", result)
    const { profile, isNewUser } = result.additionalUserInfo

    commit("setHd", profile.hd || "")
    commit("setIsNewUser", isNewUser || false)
    commit("setLocale", profile.locale || "en")
    commit("setLastName", profile.family_name)
    commit("setFirstName", profile.given_name)

    return result
  },

  /**
   * Sign in with google provider
   */
  signInWithGoogle({ dispatch }) {
    const provider = new firebase.auth.GoogleAuthProvider()
    return dispatch("signInWithPopup", provider)
  },

  /**
   * Sign in with Microsofot provider
   */
  signInWithMicrosoft({ dispatch }) {
    const provider = new firebase.auth.OAuthProvider("microsoft.com")
    return dispatch("signInWithPopup", provider)
  },

  /**
   * Generate custom token and redirect to the given URL.
   */
  async redirectWithCustomToken(context, { redirectUri, state, user }) {
    console.log("in redirect with custom token / store")

    try {
      // generate custom token for the user ID
      const userId = user.uid
      const jwt = await user.getIdToken()
      console.log("gen custom token with ", userId, jwt)
      const response = await authApi(jwt).notify("generate_custom_token", {
        userId
      })
      console.log("custom token response", response)
      console.log("custom token", response.custom_token)

      // redirect to given URL with custom token
      location.replace(
        `${redirectUri}?code=${response.custom_token}&state=${state}`
      )
    } catch (e) {
      console.error("unable to finish oauth redirect", e)
    }
  }
}

// export module
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
